import { Bell, ChevronDown, Languages, Search, X, type LucideIcon } from 'lucide-react';
import { forwardRef, useId, useState, type ComponentPropsWithoutRef, type FormEvent } from 'react';
import { cn } from '../lib/cn';
import { Button } from '../primitives/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuRadioGroup,
  DropdownMenuRadioItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../primitives/dropdown-menu';
import { Input } from '../primitives/input';
import { Avatar } from '../primitives/avatar';

// Pieces of the dashboard top bar. Pass them to AppLayout's `search` and `headerActions`.

interface HeaderSearchProps {
  /** Called with the trimmed text when the user presses Enter */
  onSearch: (query: string) => void;
  defaultValue?: string;
  labels?: { search?: string; placeholder?: string; close?: string };
}

/** Search field on desktop; on phones a button that opens the field across the top bar. */
export function HeaderSearch({ onSearch, defaultValue = '', labels = {} }: HeaderSearchProps) {
  const { search = 'Search', placeholder = 'Search…', close = 'Close search' } = labels;
  const [query, setQuery] = useState(defaultValue);
  const [open, setOpen] = useState(false);
  const inputId = useId();

  const submit = (event: FormEvent) => {
    event.preventDefault();
    const value = query.trim();
    if (value) onSearch(value);
  };

  const field = (autoFocus: boolean) => (
    <form role="search" onSubmit={submit} className="relative w-full">
      <label htmlFor={inputId} className="sr-only">
        {search}
      </label>
      <Search
        aria-hidden
        className="pointer-events-none absolute left-3.5 top-1/2 size-4 -translate-y-1/2 text-muted-foreground"
      />
      <Input
        id={inputId}
        type="search"
        value={query}
        onChange={(event) => setQuery(event.target.value)}
        onKeyDown={(event) => {
          if (event.key === 'Escape') setOpen(false);
        }}
        placeholder={placeholder}
        autoFocus={autoFocus}
        className="h-11 rounded-xl border-transparent bg-surface pl-10 shadow-sm"
      />
    </form>
  );

  return (
    <>
      <div className="hidden w-full max-w-md md:block">{field(false)}</div>
      <Button
        variant="ghost"
        size="icon"
        className="md:hidden"
        aria-label={search}
        onClick={() => setOpen(true)}
      >
        <Search />
      </Button>
      {open && (
        <div className="absolute inset-0 z-10 flex items-center gap-2 bg-surface px-4 md:hidden">
          {field(true)}
          <Button variant="ghost" size="icon" aria-label={close} onClick={() => setOpen(false)}>
            <X />
          </Button>
        </div>
      )}
    </>
  );
}

interface NotificationButtonProps extends Omit<ComponentPropsWithoutRef<'button'>, 'children'> {
  /** Unread notifications; nothing is shown at 0 */
  count: number;
  /** Full accessible name including the count, e.g. "Notifications, 3 unread" */
  label: string;
}

/** The bell with its unread count. Forwards its ref, so it can open a menu (NotificationMenu). */
export const NotificationButton = forwardRef<HTMLButtonElement, NotificationButtonProps>(
  ({ count, label, className, ...props }, ref) => (
    <Button
      ref={ref}
      variant="ghost"
      size="icon"
      className={cn('relative size-11 rounded-xl bg-surface shadow-sm hover:bg-surface', className)}
      {...props}
    >
      <Bell aria-hidden />
      <span className="sr-only">{label}</span>
      {count > 0 && (
        <span
          aria-hidden
          className="absolute right-1.5 top-1.5 min-w-4 rounded-full bg-primary px-1 text-center text-[0.625rem] leading-4 font-semibold text-primary-foreground tabular-nums ring-2 ring-surface"
        >
          {count > 99 ? '99+' : count}
        </span>
      )}
    </Button>
  ),
);
NotificationButton.displayName = 'NotificationButton';

export interface NotificationMenuItem {
  id: string;
  /** One line saying what happened: "Ny offert från Test Bygg AB" */
  title: string;
  /** The job or thing it is about */
  detail?: string;
  /** Already formatted: "5 min sedan" */
  time: string;
  unread: boolean;
  onSelect: () => void;
}

interface NotificationMenuProps {
  count: number;
  /** Newest first; the menu shows what it is given (a handful) */
  items: NotificationMenuItem[];
  onMarkAllRead?: () => void;
  onViewAll: () => void;
  /** Called when the menu opens, e.g. to load the latest items */
  onOpenChange?: (open: boolean) => void;
  loading?: boolean;
  labels: {
    /** Accessible name of the bell, including the count */
    button: string;
    title: string;
    markAllRead: string;
    viewAll: string;
    empty: string;
    unread: string;
  };
}

/** The top bar bell: unread count, the latest notifications in a menu, and links to mark all read or see all. */
export function NotificationMenu({
  count,
  items,
  onMarkAllRead,
  onViewAll,
  onOpenChange,
  loading,
  labels,
}: NotificationMenuProps) {
  return (
    <DropdownMenu onOpenChange={onOpenChange}>
      <DropdownMenuTrigger asChild>
        <NotificationButton count={count} label={labels.button} />
      </DropdownMenuTrigger>
      <DropdownMenuContent className="w-[min(22rem,calc(100vw-2rem))] p-0">
        <div className="flex items-center justify-between gap-3 px-3 py-2.5">
          <DropdownMenuLabel className="p-0 text-sm">{labels.title}</DropdownMenuLabel>
          {onMarkAllRead && count > 0 && (
            <button
              type="button"
              onClick={onMarkAllRead}
              className="rounded text-xs font-medium text-secondary underline-offset-2 hover:underline focus-visible:outline-2 focus-visible:outline-ring"
            >
              {labels.markAllRead}
            </button>
          )}
        </div>
        <DropdownMenuSeparator className="my-0" />
        <div className="max-h-96 overflow-y-auto py-1">
          {items.length === 0 ? (
            <p className="px-3 py-6 text-center text-sm text-muted-foreground">
              {loading ? '…' : labels.empty}
            </p>
          ) : (
            items.map((item) => (
              <DropdownMenuItem
                key={item.id}
                onSelect={item.onSelect}
                className="items-start gap-3 px-3 py-2.5"
              >
                <span
                  aria-hidden
                  className={cn(
                    'mt-1.5 size-2 shrink-0 rounded-full',
                    item.unread ? 'bg-primary' : 'bg-transparent',
                  )}
                />
                <span className="grid min-w-0 gap-0.5">
                  <span className={cn('text-sm', item.unread && 'font-semibold')}>
                    {item.title}
                    {item.unread && <span className="sr-only">, {labels.unread}</span>}
                  </span>
                  {item.detail && (
                    <span className="truncate text-xs text-muted-foreground">{item.detail}</span>
                  )}
                  <span className="text-xs text-muted-foreground">{item.time}</span>
                </span>
              </DropdownMenuItem>
            ))
          )}
        </div>
        <DropdownMenuSeparator className="my-0" />
        <DropdownMenuItem onSelect={onViewAll} className="justify-center py-2.5 text-sm font-medium">
          {labels.viewAll}
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

export type Language = 'sv' | 'en';

// Each language is named in itself, so people find their own whatever is selected
const languageNames: Record<Language, string> = { sv: 'Svenska', en: 'English' };

interface LanguageSwitchProps {
  value: Language;
  onChange: (language: Language) => void;
  /** "Language" / "Språk" */
  label?: string;
}

export function LanguageSwitch({ value, onChange, label = 'Language' }: LanguageSwitchProps) {
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          variant="ghost"
          className="h-11 gap-1.5 rounded-xl bg-surface px-3 shadow-sm hover:bg-surface"
        >
          <Languages aria-hidden />
          <span className="text-xs font-semibold uppercase">{value}</span>
          <span className="sr-only">
            , {label}: {languageNames[value]}
          </span>
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent>
        <DropdownMenuLabel>{label}</DropdownMenuLabel>
        <DropdownMenuRadioGroup value={value} onValueChange={(next) => onChange(next as Language)}>
          {(Object.keys(languageNames) as Language[]).map((language) => (
            <DropdownMenuRadioItem key={language} value={language} lang={language}>
              {languageNames[language]}
            </DropdownMenuRadioItem>
          ))}
        </DropdownMenuRadioGroup>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

export interface ProfileMenuItem {
  label: string;
  icon?: LucideIcon;
  onSelect: () => void;
  /** Danger items (log out) go last, after a divider */
  tone?: 'default' | 'danger';
}

interface ProfileMenuProps {
  name: string;
  /** The user's id, so the avatar stays the same when the name changes */
  avatarSeed?: string;
  email?: string;
  items: ProfileMenuItem[];
  /** "Account menu" / "Kontomeny" */
  label?: string;
}

export function ProfileMenu({ name, avatarSeed, email, items, label = 'Account menu' }: ProfileMenuProps) {
  const regular = items.filter((item) => item.tone !== 'danger');
  const danger = items.filter((item) => item.tone === 'danger');
  const renderItem = (item: ProfileMenuItem) => (
    <DropdownMenuItem key={item.label} tone={item.tone} onSelect={item.onSelect}>
      {item.icon && <item.icon aria-hidden />}
      {item.label}
    </DropdownMenuItem>
  );

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          variant="ghost"
          className="h-11 gap-2 rounded-xl bg-surface px-1.5 shadow-sm hover:bg-surface sm:pr-3"
        >
          <Avatar seed={avatarSeed} name={name} size="sm" />
          <span className="sr-only max-w-32 truncate text-sm font-medium sm:not-sr-only">{name}</span>
          <span className="sr-only">, {label}</span>
          <ChevronDown aria-hidden className="hidden size-4 text-muted-foreground sm:inline" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent className="w-56">
        <div className="px-2 py-1.5">
          <p className="truncate text-sm font-semibold">{name}</p>
          {email && <p className="truncate text-xs text-muted-foreground">{email}</p>}
        </div>
        {regular.length > 0 && <DropdownMenuSeparator />}
        {regular.map(renderItem)}
        {danger.length > 0 && <DropdownMenuSeparator />}
        {danger.map(renderItem)}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
